import {
  Fragment,
  useEffect,
  useRef,
  type ComponentType,
} from 'react';
import {
  useDispatch
} from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  withAmsSocket,
  useConcreteAmsSocket,
} from '@shared/socket';
import {
  CurrencyCodesEnum,
} from '@shared/types';
import {
  addGameWinner,
  mapGameWinner,
  setGameWinners,
  useLazyGetLatestGameWinnersQuery,
} from '@entities/game-winners';
import {
  OMEGA_AMS_WS_URL,
  TOURNAMENTS_TIMEZONE_OFFSET_HOURS
} from '../config';

dayjs.extend(utc);

export const withLatestWinners =
  <T extends object>(WrappedComponent: ComponentType<T>) =>
    withAmsSocket((props: T) => {
      const resumeTimestamp = useRef<number>(Date.now());
      const messageQueue: Array<string> = useRef([]).current;

      const dispatch = useDispatch();
      const [getLatestWinners] = useLazyGetLatestGameWinnersQuery();

      const {
        subscribe,
        connectSocket,
        disconnectSocket,
        isConnected,
      } = useConcreteAmsSocket();

      useEffect(() => {
        const updateResumeTime = () => {
          messageQueue.length = 0;
          resumeTimestamp.current = Date.now();
        };

        document.addEventListener('visibilitychange', updateResumeTime);
        window.addEventListener('focus', updateResumeTime);

        return () => {
          document.removeEventListener('visibilitychange', updateResumeTime);
          window.removeEventListener('focus', updateResumeTime);
        };
      }, []);

      useEffect(() => {
        const fetchWinners = async () => {
          const { data } = await getLatestWinners();
          if (data?.winners) { dispatch(setGameWinners(data.winners)) }
        };
        fetchWinners();
      }, [])

      useEffect(() => {
        if (!isConnected) {
          const url = new URL(OMEGA_AMS_WS_URL);
          url.searchParams.append('eventTypes', 'GameWin');
          url.searchParams.append('currencies', CurrencyCodesEnum.SCO);
          connectSocket(url.toString());
        }
      }, [isConnected, connectSocket]);

      useEffect(() => {
        if (isConnected) {
          let isDispatching = false;

          const processQueue = () => {
            if (messageQueue.length === 0) {
              isDispatching = false;
              return;
            }

            const message = messageQueue.shift();
            if (message) {
              try {
                const parsedMessage = JSON.parse(message);

                const eventTime = dayjs
                  .utc(parsedMessage?.date)
                  .add(Number(TOURNAMENTS_TIMEZONE_OFFSET_HOURS), 'hour')
                  .valueOf();

                if (eventTime >= resumeTimestamp.current) {
                  dispatch(addGameWinner(mapGameWinner(parsedMessage)));
                }

              } catch (err) {
                console.error('[withLatestWinners] Failed to parse incoming data:', err);
              }
            }

            setTimeout(processQueue, 300);
          };

          const unsubscribe = subscribe((data: any) => {
            messageQueue.push(data.data);
            if (!isDispatching) {
              isDispatching = true;
              processQueue();
            }
          });
          return () => { unsubscribe() };
        }
      }, [isConnected, subscribe, dispatch]);

      useEffect(() => {
        return () => { disconnectSocket() };
      }, [disconnectSocket]);

      return (
        <Fragment>
          <WrappedComponent {...props} />
        </Fragment>
      );
    });